import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExtensionHint1 } from '../../assets/ExtensionHint1';
import { ExtensionHint2 } from '../../assets/ExtensionHint2';
import { PartyState, useEventStream } from './useEventStream';
import * as ackLinkApi from '../../api/ack-link';
import { ExtensionHintSettings, getUserInfo, IFrameEvent, MessageAndSoundEvent, OverlayEvent, PopupSettings, PositionSettings } from '../../api/ack-link';
import * as Sentry from '@sentry/react';
import { ErrorBoundary } from '@sentry/react';
import { useParams } from 'react-router-dom';
import { UnauthorizedException } from '../../exceptions/UnauthorizedException';
import { ResourceNotFoundException } from '../../exceptions/ResourceNotFoundException';
import { FailedToFetchException } from '../../exceptions/FailedToFetchException';
import { useTitsInteraction } from './useTitsInteraction';
import { localStorage } from '../../utility/local-storage';
import { reportMetric } from '../../api/metrics';
import { Grid } from './Grid';
import { RestrictedZonesPreview } from './RestrictedZonesPreview';
import { Dungeon } from './Dungeon';
import { useSharedFunction } from 'frontlink';
import { addQueryParams } from '../../utility/addQueryParams';
import tangiaPaused from '../../assets/tangia-paused.png';

const extensionHintDefault: ExtensionHintSettings = { Enabled: false, SecondsOn: 10, SecondsOff: 30 };
Object.freeze(extensionHintDefault);
const dungeonDefault = { Enabled: false, Position: { Y: 400, X: 1400, Width: 900 / 2, Height: 1050 / 2 } };
Object.freeze(dungeonDefault);
const popupSettingsDefault: PopupSettings = {
	Enabled: true,
	FontColor: { Red: 255, Green: 255, Blue: 255, Alpha: 1 },
	BorderColor: { Red: 236, Green: 236, Blue: 236, Alpha: 1 },
	BackgroundColor: { Red: 50, Green: 50, Blue: 50, Alpha: 1 },
	IconColor: { Red: 255, Green: 255, Blue: 255, Alpha: 1 },
	IconBackgroundColor: { Red: 248, Green: 81, Blue: 28, Alpha: 1 },
	Size: 'l',
	Sound: 'muted',
	TimeSeconds: 5,
	X: 1240,
	Y: 600,
	Height: 400,
	Width: 600,
	Volume: 70,
};
Object.freeze(popupSettingsDefault);
const partySettingsDefault: PositionSettings = { X: 1400, Y: 30, Height: 200, Width: 500 };
Object.freeze(partySettingsDefault);
const spotSettingsDefault: PositionSettings = { X: 50, Y: 50, Height: 800, Width: 800 };
Object.freeze(spotSettingsDefault);

const backendStreamType = 'overlay';
const titsIntegration = 'ol_tits';
const dungeonIntegration = 'ol_dungeon';
const NOT_InteractionsIntegration = '!interactions';

interface OverlayEventWithCallbacks {
	instanceID: string;
	data: MessageAndSoundEvent | IFrameEvent;
	interactionSettings?: OverlayEvent['InteractionSettings'];
	overlaySettings?: OverlayEvent['OverlaySettings'];
	debugMode?: boolean;
	onEnded: Function;
	onPlaying?: Function;
	onFailed: (reason: string) => void;
}

const searchParams = new URLSearchParams(window.location.search);
const featuresList = searchParams.get('features');
const audioOnlyMode = searchParams.get('audio_only') === 'true';
const developerMode = searchParams.get('debug') === 'true';

console.log('overlay starting with', 'features', featuresList, 'audioOnlyMode', audioOnlyMode);

export const StreamFrame = () => {
	const { streamKey } = useParams();

	const { connected: titsConnected } = useTitsInteraction();
	const [dungeonConnected, setDungeonConnected] = useState(false);
	const [debugMode, setDebugMode] = useState(false);
	const [devInfo, _setDevInfo] = useState('');
	// prevent re-renders on non-dev mode
	const setDevInfo = developerMode ? _setDevInfo : () => {};
	const [shouldPause, setShouldPause] = useState(false);
	const [shouldShowPause, setShouldShowPause] = useState(true);
	const [extensionHintSettings, setExtensionHintSettings] = useState(extensionHintDefault);
	const [dungeonSettings, setDungeonSettings] = useState(dungeonDefault);
	const popupSettings = useRef(popupSettingsDefault);
	const spotSettings = useRef(spotSettingsDefault);
	const [partySettings, setPartySettings] = useState(partySettingsDefault);
	const [eventsQueue, setEventsQueue] = useState<OverlayEventWithCallbacks[]>([]);
	const playingEventID = useRef<string>();
	const volumeSetting = useRef(0.7);
	const iframeSkipResolve = useRef<(reason: string) => void>();

	const [userID, setUserID] = useState<string>();
	const [retryCount, setRetryCount] = useState(0);

	const enabledFeatures = useEnabledFeatures(userID, featuresList);

	useEffect(() => {
		if (!streamKey) {
			return;
		}
		(async () => {
			try {
				const res = await getUserInfo(streamKey);
				setUserID(res.UserID);
			} catch (err) {
				console.error('Error getting user info', err);
				if (err instanceof UnauthorizedException) {
					console.error('Unauthorized exception - stopping user info polling', err);
					return;
				}
				await new Promise((r) => setTimeout(r, 3_000));
				setRetryCount((prev) => prev + 1);
			}
		})();
	}, [streamKey, retryCount]);

	const pollSettings = useCallback(async () => {
		if (!streamKey) return;
		try {
			console.log('polling settings');
			const settings = await ackLinkApi.getSettings(streamKey);
			setExtensionHintSettings((old) => settings.ExtensionHint ?? old);
			if (settings.Dungeon) {
				setDungeonSettings(settings.Dungeon);
			}
			popupSettings.current = settings.Popup;
			const newVol = settings.Volume / 100;
			if (volumeSetting.current !== newVol) {
				volumeSetting.current = newVol;
			}
			if (settings.Spot) {
				spotSettings.current = settings.Spot;
			}
			if (settings.ChaosGoal) {
				setPartySettings(settings.ChaosGoal);
			}
			const pauseActive = !!settings.InteractionsPausedUntil && new Date(settings.InteractionsPausedUntil) > new Date();
			setShouldPause(pauseActive);
			setShouldShowPause(!!settings.ShouldShowPauseIndicator);
		} catch (err) {
			setDevInfo((old) => old + `\n${err}`);
			if (err instanceof UnauthorizedException) {
				console.error('Unauthorized exception.', err);
			} else if (err instanceof ResourceNotFoundException) {
				console.error('Styling resource not found exception.');
			} else if (err instanceof FailedToFetchException) {
				// Nothing to do, just some network interruption
			} else {
				console.error(err);
				Sentry.captureException(err);
			}
		}
	}, [streamKey]);

	const { partyState, showParty, showPartyGoalExceeded, partyStatePercent } = useEventStream({
		authKey: streamKey,
		onConnecting: pollSettings,
		onChaosMessage: pollSettings,
		onSettingsMessage: pollSettings,
		onPausingMessage: (msg) => {
			console.log('pausing interactions', msg.ShouldPause);
			setShouldPause(msg.ShouldPause);
			setShouldShowPause(msg.ShouldShow);
		},
		onSkipMessage: (msg) => {
			console.log('got skip signal interaction', msg);
			// we might get this while the event isn't actually on screen
			if (msg.EventID && playingEventID.current !== msg.EventID) {
				return;
			}
			// we just resolve the iframe promise, so it gets handled like an ordinary completion
			iframeSkipResolve.current?.('skipped');
		},
		onInteractionMessage: () => console.log('not implemented yet'),
	});

	useEffect(() => {
		if (!streamKey) return;
		console.log('Starting liveness loop', 'dungeon', dungeonConnected, dungeonSettings, 'tits', titsConnected);
		// ensure (async) that we got the settings at least once
		pollSettings();
		let alive = true;
		const pingBackend = async () => {
			if (enabledFeatures.interactions) {
				try {
					await ackLinkApi.postStart(streamKey!, backendStreamType);
				} catch (err: any) {
					if (!(err instanceof FailedToFetchException) && !(err instanceof UnauthorizedException)) {
						Sentry.captureException(err);
					}
					console.error('error sending start event to backend', err);
				}
			}
			while (alive) {
				const onlineSubIntegrations = [];
				if (titsConnected) {
					onlineSubIntegrations.push(titsIntegration);
				}
				if (dungeonSettings.Enabled && dungeonConnected && enabledFeatures.dungeon) {
					onlineSubIntegrations.push(dungeonIntegration);
				}
				// since the interactions are the default feature, and for backwards compatibility, we use a negated integration ID
				if (!enabledFeatures.interactions) {
					onlineSubIntegrations.push(NOT_InteractionsIntegration);
				}
				try {
					await ackLinkApi.pingBackend(streamKey!, backendStreamType, onlineSubIntegrations, playingEventID.current);
				} catch (err: any) {
					if (err instanceof UnauthorizedException) {
						setEventsQueue([
							{
								instanceID: new Date().toString(),
								data: {
									type: 'overlay_popup',
									URL: 'https://alertsinteraction.pages.dev/',
									OverlayParams: {
										text: 'Your overlay key is invalid! 😞\nYou should go to the Tangia dashboard and set up the overlay again 🍊',
										durationMS: popupSettings.current.TimeSeconds * 5_000,
										overlaySettings: {
											Popup: popupSettings.current,
										},
									},
								},
								onEnded: () => {
									setEventsQueue([]);
								},
								onFailed: () => {
									setEventsQueue([]);
								},
							},
						]);
					} else if (!(err instanceof FailedToFetchException)) {
						Sentry.captureException(err);
					}
					console.error('error pinging backend', err);
				}
				await new Promise((r) => setTimeout(r, 9_500)); // the backend has a 30s timeout, so with a little less than 3s, we have 3 chances to stay alive
			}
		};
		pingBackend();
		return () => {
			console.log('Stopping liveness loop');
			alive = false;
		};
	}, [streamKey, titsConnected, dungeonSettings.Enabled, dungeonConnected, enabledFeatures]);

	useEffect(() => {
		const interactionsEnabled = enabledFeatures.interactions || enabledFeatures['interactions:custom'];
		if (!streamKey || shouldPause || !interactionsEnabled) {
			console.log('Pausing stream frame events polling loop');
			// this kills the iframe and immediately stops the interaction. When we get un-paused, that interaction will happen
			// again as we didn't ack it and it should still be in the queue
			setEventsQueue([]);
			Object.keys(dedupeMap).forEach((k) => delete dedupeMap[Number(k)]);
			return;
		}
		console.log('Starting stream frame events polling loop');
		let polling = true;

		const codeReloadDeadline = new Date().getTime() + 10 * 60 * 1000;
		const pollEvents = async () => {
			while (polling) {
				const start = new Date();
				// every few minutes we refresh the page with a cache-busting URL change to ensure live streamers do receive updates
				if (start.getTime() > codeReloadDeadline) {
					// Disabling refreshing until we have found a solution that doesn't break the Tangia Grid experience
					// await refreshPage(start);
				}
				try {
					const response = await ackLinkApi.getPollEvent(streamKey!, backendStreamType);
					const end = new Date();
					const pollTime = end.getTime() - start.getTime();
					console.log(`\tPolled in ${pollTime}ms`);

					if (!response?.Events?.length) {
						if (pollTime < 5_000) {
							console.log('poll was too fast, sleeping for 5s', pollTime);
							await new Promise((resolve) => setTimeout(resolve, 5_000 - pollTime));
						}
						continue;
					}
					console.log(`Received ${response.Events.length} events`);

					for (let i = 0; i < response.Events.length; i++) {
						const eventContainer = response.Events[i];
						if (!polling) {
							return;
						}
						const eventKinds = ackLinkApi.getEventKinds(eventContainer.Data);
						const isCustom = eventKinds.includes('custom');
						const shouldPlayEvent = (isCustom && enabledFeatures['interactions:custom']) || (!isCustom && enabledFeatures.interactions);
						if (!shouldPlayEvent) {
							console.log(
								`skipping event with wrong kind: isCustom=${isCustom} customFeature=${enabledFeatures['interactions:custom']} interactionsFeature=${enabledFeatures.interactions}`,
							);
							// no need to instantly poll again
							await new Promise((resolve) => setTimeout(resolve, 1_000));
							continue;
						}
						if (eventContainer.EventID && eventContainer.UnixMS && dedupeMap[eventContainer.UnixMS]) {
							console.log('Already received, skipping', eventContainer.UnixMS, eventContainer.EventID);
							// we are going to "skip" it for this reason
							await confirmEventHandled('skip', eventContainer, 'de-dupe');
							continue;
						}
						Sentry.setExtra('eventItem', JSON.stringify(eventContainer));
						playingEventID.current = eventContainer.EventID;
						const [skipProm, skipResolve] = newSplitPromise<string>();
						iframeSkipResolve.current = skipResolve;

						const curPopupSettings = eventContainer.OverlaySettings?.Popup || popupSettings.current;
						const popupSound =
							curPopupSettings.Sound === 'muted'
								? undefined
								: curPopupSettings.Sound?.startsWith('http')
									? curPopupSettings.Sound
									: 'https://assets.tangia.co/sounds/' + curPopupSettings.Sound;

						// normalize individual events, groups and the fake composer test
						let events =
							eventContainer.Data.type === 'group'
								? eventContainer.Data.Events
								: eventContainer.Data.type === 'overlay_composer'
									? [
											{
												NonBlocking: true,
												Event: eventContainer.Data.overlay_spot,
											},
											{
												NonBlocking: true,
												Event: eventContainer.Data.overlay_popup,
											},
										]
									: [
											{
												NonBlocking: false,
												Event: eventContainer.Data,
											},
										];
						// handling popups
						events = events.flatMap((e) => {
							// popups get converted to alert interactions
							if (e.Event.type === 'popup') {
								return [
									{
										NonBlocking: false,
										Event: {
											type: 'overlay_popup',
											URL: 'https://alertsinteraction.pages.dev/',
											OverlayParams: {
												text: e.Event.Message ?? '',
												soundURL: e.Event.Sound || popupSound,
												durationMS: curPopupSettings.TimeSeconds * 1_000,
												overlaySettings: {
													Popup: curPopupSettings,
												},
											},
										},
									},
								];
							}
							// normal iframe interactions stay as is
							if (!e.Event.Message) {
								return [e];
							}
							// iframe interactions that also want to display a message get converted to a concurrent popup (alert interaction)
							// plus the original iframe interaction
							return [
								{
									NonBlocking: true,
									Event: {
										type: 'overlay_popup',
										URL: 'https://alertsinteraction.pages.dev/',
										OverlayParams: {
											text: e.Event.Message,
											soundURL: popupSound,
											durationMS: curPopupSettings.TimeSeconds * 1_000,
											overlaySettings: {
												Popup: curPopupSettings,
											},
										},
									},
								},
								e,
							];
						});
						setDebugMode(eventContainer.Data.type === 'overlay_composer');
						setEventsQueue([]);
						let result: any = '';
						let failureReason = '';
						let retriesRemaining = eventContainer.Attempts ?? 1;
						console.log('processing events', events.length);
						let startedPlaying = false;
						try {
							let endedPromises: Promise<any>[] = [];
							let eventIdx = 0;
							for (const event of events) {
								eventIdx++;
								const [ended, resolve, reject] = newSplitPromise();
								// prevent the rejection to lead to unhandledrejection errors
								ended.catch((e) => console.log('interaction event failed', e));
								endedPromises.push(ended);
								const eventKey = `${eventContainer.EventID ?? eventContainer.UnixMS}_${eventIdx}_${retriesRemaining}`;
								console.log('queueing event', eventKey, event.Event);
								setDevInfo((old) => old + `\nnew event: ${eventKey}}`);
								setEventsQueue((old) => [
									...old,
									{
										instanceID: eventKey,
										data: event.Event,
										interactionSettings: eventContainer.InteractionSettings,
										overlaySettings: eventContainer.OverlaySettings,
										onPlaying: () => (startedPlaying = true),
										onEnded: resolve,
										onFailed: reject,
									},
								]);
								if (!event.NonBlocking) {
									await Promise.race([skipProm, ended]);
									setEventsQueue((old) => old.slice(0, -1));
								}
							}
							const allDone = Promise.all(endedPromises);
							result = await Promise.race([skipProm, allDone]);
						} catch (err: any) {
							if (typeof err.toString !== 'undefined') {
								failureReason = err.toString();
								if (/\[object.*]/.test(failureReason)) {
									// didn't have a real "toString" implementation, so just JSON serialize it
									try {
										const json = Object.assign({}, err);
										failureReason = JSON.stringify(json);
									} catch (jsonErr: any) {
										console.error('json fallback failed', jsonErr);
									}
								}
							}
							setDevInfo((old) => old + `\n${err}`);
						} finally {
							setEventsQueue([]);
						}
						if (failureReason && retriesRemaining > 0 && !startedPlaying) {
							console.log('failed to handle event, retrying...', failureReason);
							retriesRemaining--;
							eventContainer.Attempts = retriesRemaining;
							// retry current loop iteration
							i--;
							continue;
						}
						const action = failureReason ? 'nack' : result === 'skipped' ? 'skip' : 'ack';
						try {
							console.log(`${action}ing event`, eventContainer);
							await confirmEventHandled(action, eventContainer, failureReason);
						} catch (err) {
							setDevInfo((old) => old + `\n${err}`);
							console.error('ackEvent error hit');
							console.error(err);
							if (!(err instanceof FailedToFetchException)) {
								Sentry.captureException(err);
							}
							await new Promise((r) => setTimeout(r, 10_000));
						}
						if (failureReason) {
							console.log('overlay error', failureReason, eventContainer);
							Sentry.setExtra('eventItem', JSON.stringify(eventContainer));
							Sentry.captureMessage(failureReason === 'timeout' ? 'Hit iframe timeout' : 'Hit overlay error', {
								level: 'error',
								extra: {
									eventID: eventContainer.EventID,
									failureReason,
								},
							});
						}
					}
				} catch (err) {
					setDevInfo((old) => old + `\n${err}`);
					if (err instanceof UnauthorizedException) {
						console.error('Unauthorized exception.');
						await new Promise((r) => setTimeout(r, 60_000));
					} else if (err instanceof FailedToFetchException) {
						console.log('Hit failed to fetch error. Sleeping 10 seonds');
						await new Promise((r) => setTimeout(r, 10_000));
					} else {
						console.error('pollEvents unknown error hit. Sleeping 10 seonds');
						console.error(err);
						Sentry.captureException(err);
						await new Promise((r) => setTimeout(r, 10_000));
					}
				} finally {
					playingEventID.current = undefined;
					setDebugMode(false);
				}
			}
		};
		pollEvents();

		return () => {
			console.log('Stopping stream frame events polling loop');
			polling = false;
		};
	}, [streamKey, shouldPause, enabledFeatures.interactions, enabledFeatures['interactions:custom']]);

	if (isOutdatedBrowser()) {
		return <OutdatedBrowserWarning />;
	}
	return (
		<div
			className="relative h-full bg-transparent"
			style={{
				backgroundImage: debugMode ? 'linear-gradient(45deg, transparent 45%, rgb(208 119 48 / 81%) 46%, rgb(208 119 48 / 81%) 54%, transparent 55%)' : 'none',
				backgroundSize: '34px 34px',
			}}
		>
			{developerMode && <div className="absolute top-0 left-0 bg-red-500 text-white text-2xl p-10">DEBUG MODE: {devInfo}</div>}
			<ErrorBoundary
				onError={(err: any) => {
					iframeSkipResolve.current?.('skip');
					console.error('error in rendering', err);
					Sentry.captureException(err);
					Sentry.captureMessage(`Error Boundary triggered ${err.message}`, 'error');
				}}
			>
				{userID && enabledFeatures.grid && <Grid userID={userID} />}
				{userID && <RestrictedZonesPreview userID={userID} />}
				{userID && enabledFeatures.dungeon && dungeonSettings.Enabled && (
					<Dungeon
						userID={userID}
						streamKey={streamKey!}
						iframeStyle={createIframeStyle('dungeon', spotSettings.current, popupSettings.current, undefined, dungeonSettings.Position)}
						onLoaded={() => setDungeonConnected(true)}
					/>
				)}
				{!shouldPause && !showPartyGoalExceeded && eventsQueue?.length ? (
					eventsQueue.map((event) => (
						<EventHandler
							{...event}
							key={event.instanceID}
							debugMode={debugMode}
							popupSettings={popupSettings}
							spotSettings={spotSettings}
							volumeSetting={volumeSetting}
							streamKey={streamKey!}
							userID={userID!}
						/>
					))
				) : enabledFeatures.interactions ? (
					<>
						<PauseOverlay shouldPause={shouldPause && shouldShowPause} popupSettings={popupSettings} />
						<ExtensionHintOverlay settings={extensionHintSettings} />
						<TangiaParties
							state={partyState}
							show={showParty}
							showGoalExceeded={showPartyGoalExceeded}
							statePercent={partyStatePercent}
							settings={partySettings}
						/>
					</>
				) : null}
				{enabledFeatures.subathon ? (
					<iframe
						className="w-screen h-screen absolute top-0 left-0 overflow-hidden"
						src={`${import.meta.env.VITE_SUBATHON_URL}?key=${streamKey}`}
						scrolling="off"
					/>
				) : null}
				<RequiredResolutionCheck />
			</ErrorBoundary>
		</div>
	);
};

const overlayInstanceID = 'oi_' + (Math.random() * 1_000_000_000_000).toFixed(0);

type Feature = 'dungeon' | 'interactions' | 'grid' | 'subathon' | 'interactions:custom';

const pingTimeout = 15_000;

function useEnabledFeatures(userID?: string, features?: string | null) {
	const featureListWithNegated = features?.split(',').map((f) => f.trim()) as Array<Feature | `!${Feature}`> | undefined;
	const featureList = featureListWithNegated?.filter((f) => !f.startsWith('!')) as Array<Feature> | undefined;
	// since this was introduced later, and there are people out there who already have an isolated browser source for interactions,
	// we default to interactions=all to not break their setup
	if (featureList?.includes('interactions') && !featureListWithNegated?.includes('!interactions:custom')) {
		featureList.push('interactions:custom');
	}

	const [enabledFeatures, setEnabledFeatures] = useState<Record<Feature, boolean>>({
		dungeon: !featureList || featureList.includes('dungeon'),
		interactions: !featureList || featureList.includes('interactions'),
		'interactions:custom': !featureList || featureList.includes('interactions:custom'),
		grid: !featureList || featureList.includes('grid'),
		subathon: !featureList || featureList.includes('subathon'),
	});
	const stateRef = useRef({ enabledFeatures });
	stateRef.current.enabledFeatures = enabledFeatures;

	const lastPing = useRef<Record<Feature, number>>({ dungeon: 0, interactions: 0, 'interactions:custom': 0, grid: 0, subathon: 0 });

	const ping = useSharedFunction(`overlayFeaturePing::${userID}`, (instanceID: string, instanceFeatureList: Feature[]) => {
		if (instanceID === overlayInstanceID || !!featureList) {
			// we can ignore our own pings and generally if we are not the default overlay
			return;
		}
		// console.log("got ping from", instanceID, instanceFeatureList, "we are", overlayInstanceID);
		// we disable the features that are active on another overlay
		for (const feature of instanceFeatureList) {
			lastPing.current[feature] = new Date().getTime();
			// we check the previous state so we don't cause constant React re-renders
			if (stateRef.current.enabledFeatures[feature]) {
				setEnabledFeatures((old) => {
					const newFeatures = { ...old, [feature]: false };
					console.log('enabled features', newFeatures);
					return newFeatures;
				});
			}
		}
	});

	useEffect(() => {
		if (!userID) {
			return;
		}
		console.log('enabled features', enabledFeatures);
		if (featureList) {
			// if we get a feature list, that means those are always enabled, and we have to ping
			// the default overlay, so its doesn't enable them
			const pingInterval = setInterval(() => {
				ping(overlayInstanceID, featureList);
			}, pingTimeout / 3);
			ping(overlayInstanceID, featureList);
			return () => {
				clearInterval(pingInterval);
			};
		} else {
			// no feature list means, we are the default overlay, so everything is enabled
			// unless the feature got a ping (from another overlay) recently
			const checkPingsInterval = setInterval(() => {
				const now = new Date().getTime();
				for (const feature of Object.keys(lastPing.current) as Feature[]) {
					if (
						lastPing.current[feature] + pingTimeout < now &&
						// we check the previous state so we don't cause constant React re-renders
						!stateRef.current.enabledFeatures[feature]
					) {
						setEnabledFeatures((old) => {
							const newFeatures = { ...old, [feature]: true };
							console.log('enabled features', newFeatures);
							return newFeatures;
						});
					}
				}
			}, pingTimeout / 10);
			return () => {
				clearInterval(checkPingsInterval);
			};
		}
	}, [userID]);

	return enabledFeatures;
}

function EventHandler(
	props: OverlayEventWithCallbacks & {
		popupSettings: RefObject<typeof popupSettingsDefault>;
		spotSettings: RefObject<typeof spotSettingsDefault>;
		volumeSetting: RefObject<number>;
		streamKey: string;
		userID: string;
	},
) {
	if ('URL' in props.data) {
		return <IFrameEventHandler {...props} data={props.data} />;
	} else {
		console.log('Received unhandled event', props.data);
		Sentry.setExtra('eventItem', JSON.stringify(props.data));
		Sentry.captureMessage('Received unhandled event', 'error');
		props.onFailed('unhandled');
		return null;
	}
}

export interface MetricEvent {
	type: 'metric';
	metric: string;
	value: number;
}

function IFrameEventHandler(
	props: OverlayEventWithCallbacks & {
		popupSettings: RefObject<typeof popupSettingsDefault>;
		spotSettings: RefObject<typeof spotSettingsDefault>;
		volumeSetting: RefObject<number>;
		streamKey: string;
		userID: string;
	} & { data: IFrameEvent },
) {
	const { data, onEnded, onPlaying, onFailed, popupSettings, spotSettings, volumeSetting, streamKey, userID } = props;

	const iframeRef = useRef<HTMLIFrameElement>(null);

	const [startedToPlay, setStartedToPlay] = useState(false);
	const [duration, setDuration] = useState(data.MaxDuration || 60);
	const maxDurationSecs = duration + 65;

	// for iframe timeouts
	useEffect(() => {
		console.log(`Giving iframe max ${maxDurationSecs}s to complete`, new Date());
		const timeout = setTimeout(() => onFailed('timeout'), maxDurationSecs * 1_000);

		return () => {
			clearTimeout(timeout);
		};
	}, [data, startedToPlay, maxDurationSecs]);

	// for iframe events
	useEffect(() => {
		function handleIFrameMessage(event: MessageEvent) {
			// we now have (potentially) multiple iframes, and it's not possible to register only for one
			if (event.source !== iframeRef.current?.contentWindow) {
				return;
			}
			switch (event.data.status) {
				case 'readyToConfig':
					// per-interaction volume override, new overlay volume, or last volume we got globally
					const volume =
						props.interactionSettings?.Volume !== undefined
							? props.interactionSettings?.Volume / 100
							: props.overlaySettings?.Volume !== undefined
								? props.overlaySettings?.Volume / 100
								: volumeSetting.current;
					const configMessage = {
						cmd: 'config',
						config: {
							...data.OverlayParams,
							settings: props.interactionSettings,
							overlaySettings: props.overlaySettings,
						},
						volume: volume,
						mirrored: data.Placement?.Mirrored,
					};
					console.log('Received readyToConfig', data, 'configMessage: ', configMessage);
					iframeRef.current!.contentWindow!.postMessage(configMessage, '*');
					break;

				case 'duration_update':
					console.log('Received duration update', event.data);
					setDuration(event.data.duration);
					break;

				case 'readyToPlay':
					console.log('Received readyToPlay', data);
					iframeRef.current!.contentWindow!.postMessage({ cmd: 'play' }, '*');
					break;

				case 'playing':
					console.log('Received playing', data);
					setStartedToPlay(true);
					onPlaying?.();
					break;

				case 'ended':
					console.log('Received ended', data);
					onEnded();
					break;

				case 'error':
					console.log('Received error', event.data, data);
					onFailed(event.data);
					reportMetric({
						event_key: streamKey ?? '',
						created_ms: new Date().getTime(),
						url: data.URL,
						metric: 'iframe_error',
						value: 1,
						data: {
							eventData: data, // maybe we need to reduce this
							error: event.data,
						},
					});
					break;

				case 'metric':
					const metricEvent = event.data as MetricEvent;
					reportMetric({
						event_key: streamKey ?? '',
						created_ms: new Date().getTime(),
						url: data.URL,
						metric: metricEvent.metric,
						value: metricEvent.value,
						data: data, // maybe we need to reduce this
					});
					break;

				default:
					console.warn('Unknown message from contents:', event.data);
					break;
			}
		}

		window.addEventListener('message', handleIFrameMessage);
		return () => {
			window.removeEventListener('message', handleIFrameMessage);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const authParams = data.URL.includes('auth-pass-through')
		? {
				key: props.streamKey,
				userID: props.userID,
				backendAPI: import.meta.env.VITE_BACKEND_API,
			}
		: undefined;

	return (
		<iframe
			className={
				(props.debugMode && 'border-2 border-red-600 bg-orange-950 bg-opacity-70') +
				' w-screen h-screen fixed top-0 left-0 overflow-hidden origin-top-left bg-transparent scale-100' +
				(audioOnlyMode ? ' !opacity-0' : '')
			}
			src={addQueryParams(data.URL, { audio_only: audioOnlyMode, debug: developerMode, ...authParams })}
			scrolling="off"
			style={createIframeStyle(
				data.OverlayParams?.TriggerData?.OverlayTypeOverride || data.type,
				spotSettings.current!,
				popupSettings.current!,
				data.Placement,
			)}
			ref={iframeRef}
			allow="autoplay *; encrypted-media *;"
			title="Stream Frame"
			onError={handleIFrameError}
		></iframe>
	);
}

function handleIFrameError(event: any) {
	console.error('Iframe onError function called');
	console.error(event);
	Sentry.captureMessage(`Iframe Error Event ${event.message}`, 'error');
}

function TangiaParties(props: {
	state: PartyState | null;
	statePercent: number | null;
	showGoalExceeded: boolean;
	show: boolean;
	settings: typeof partySettingsDefault;
}) {
	const { state, statePercent, show, showGoalExceeded, settings } = props;
	const mountedStyle = { animation: 'inAnimation 500ms ease-in' };
	const unmountedStyle = { animation: 'outAnimation 510ms ease-in' };
	const shouldRenderChaosState = useDelayUnmount(show, 500);
	const chaosToastStyle = useMemo(() => {
		const s = settings;
		return {
			width: '500px',
			// height: isExceededShown ? "250px" : "250px",
			height: '250px',
			top: `${s.Y}px`,
			left: `${s.X}px`,
			transform: `scale(${s.Width / 500})`,
			transformOrigin: `top left`,
		};
	}, [settings]);

	if (!shouldRenderChaosState || !state || !state.Enabled) {
		return null;
	}
	return (
		<>
			{showGoalExceeded ? (
				<div
					className="fixed z-[9999999999999] w-[500px] h-[300px] overflow-hidden"
					style={{
						...chaosToastStyle,
						...(show ? mountedStyle : unmountedStyle),
					}}
				>
					<div className="w-full p-3 border bg-gradient-to-b from-[#FF7A00] to-[#EB5220] flex items-center justify-start h-auto relative rounded-2xl overflow-hidden gap-3">
						<img src="/chaos-bg.svg" alt="ethereum icon" className="w-full h-full object-cover absolute top-0 left-0" />
						<div
							className="bg-[#fff] rounded-lg min-w-[188px] h-[64px] flex items-center justify-center p-1.5 leading-none relative z-20"
							style={{
								boxShadow: '-5px 0px 16px rgba(0, 0, 0, 0.24)',
							}}
						>
							<div>
								<span className="text-gradient-primary text-[40px] leading-none font-bold">LVL {state?.Level?.toLocaleString() || 0}</span>
								<span className="text-gradient-primary text-[22px] font-bold pl-2.5">REACHED</span>
							</div>
						</div>
						<div className="flex items-center gap-2 relative z-20">
							<div className="flex items-center gap-1 flex-col">
								<img src="/icons/tangia-coin.svg" alt="tangia coin" className="w-[30px] h-[30px]" />
								<img src="/icons/ethereum.png" alt="ethereum icon" className="w-[30px] h-[30px]" />
							</div>
							<div className="text-[white] text-[40px] whitespace-nowrap">
								<span className="font-bold">{state?.Threshold.toLocaleString()}</span>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div
					className={`fixed z-[99999]`}
					style={{
						...chaosToastStyle,
						...(show ? mountedStyle : unmountedStyle),
					}}
				>
					<div className="w-full shrink">
						<div className="text-[#fff] text-[32px] font-bold drop-shadow-lg ml-[44px] leading-none mb-0.5">TANGIA PARTY</div>
						<div
							className="mb-3 w-[calc(100%-70px)] h-[40px] rounded-lg bg-[#F3A890] relative rounded-r-none"
							style={{
								boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
							}}
						>
							<div
								className="absolute top-0 left-0 h-full bg-primary-500 transition-[width] duration-200 rounded-lg rounded-r-none"
								style={{
									width: `${statePercent}%`,
									background: 'linear-gradient(180deg, #FF7A00 0%, #EB5220 100%)',
								}}
							></div>
							<div className="absolute top-1/2 !leading-none text-[22px] right-4 text-[#fff] -translate-y-1/2 font-bold whitespace-nowrap">
								<div className="flex items-center gap-3 shrink-0">
									<div className="flex items-center gap-1">
										<img src="/icons/ethereum.png" alt="ethereum icon" className="w-6 h-6 max-w-[unset]" />
										<img src="/icons/tangia-coin.svg" alt="tangia coin" className="w-6 h-6 max-w-[unset]" />
									</div>
									<div className="text-[#fff] text-[22px] drop-shadow-lg">
										<span className="font-bold">{state?.Current.toLocaleString()}</span> / {state?.Threshold.toLocaleString()}
									</div>
								</div>
							</div>

							<svg xmlns="http://www.w3.org/2000/svg" width={70} height={45} fill="none" className="absolute top-0 right-3 translate-y-[calc(-100%+1px)]">
								<path fill="#212020" d="M23 26h27v11H23z" />
								<path
									fill="url(#a)"
									d="M69.025 41.946c-2.75-14.152-14.137-25.325-28.39-27.726l.832-4.566c2.703 1.197 5.752.846 7.961-1.068l3.615-3.164-3.874-2.397c-2.292-1.356-5.12-.737-6.84 1.224l.55-3.16-3.776-.312-.735 6.652a5.835 5.835 0 0 0-2.988-1.323l-3.428-.328.698 3.032c.445 2.07 2.595 3.363 4.664 2.918l.758-.175-.25 2.102c-.98-.094-1.96-.188-3.002-.139C17.758 13.614 3.553 25.701.68 41.787v2.147l68.345.116v-2.104Zm-43.186-7.392-1.043.049.278-6.004 4.597-.005.007 6.13c-1.327-.078-2.654-.155-3.84-.17Zm18.184.059c-1.327-.078-2.575.05-3.823.178l-.15-6.193 4.74.058.213 6.05c-.285-.126-.632-.11-.98-.093Z"
								/>
								<defs>
									<linearGradient id="a" x1={34.852} x2={34.852} y1={0.777} y2={44.05} gradientUnits="userSpaceOnUse">
										<stop stopColor="#FF7A00" />
										<stop offset={1} stopColor="#EB5220" />
									</linearGradient>
								</defs>
							</svg>
							<div
								className="absolute top-1/2 right-0 bg-[#fff] rounded-lg w-[70px] h-[70px] flex items-center justify-center p-3 flex-col font-bold leading-none mt-[-35px] mr-[-70px]"
								style={{
									boxShadow: '-5px 0px 16px rgba(0, 0, 0, 0.24)',
								}}
							>
								<div className="text-gradient-primary text-xl leading-none">LVL</div>
								<div className="text-gradient-primary text-4xl">{(state?.Level || 0) + 1}</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

function useDelayUnmount(isMounted: boolean, delayTime: number) {
	const [shouldRender, setShouldRender] = useState(false);

	useEffect(() => {
		let timeoutId: number;
		if (isMounted && !shouldRender) {
			setShouldRender(true);
		} else if (!isMounted && shouldRender) {
			timeoutId = setTimeout(() => setShouldRender(false), delayTime);
		}
		return () => clearTimeout(timeoutId);
	}, [isMounted, delayTime, shouldRender]);

	return shouldRender;
}

function ExtensionHintOverlay(props: { settings: typeof extensionHintDefault }) {
	const { settings } = props;
	const [showExtensionHint, setShowExtensionHint] = useState(false);
	const [extensionHintVersion, setExtensionHintVersion] = useState(0);

	useEffect(() => {
		if (!settings.Enabled || !settings.SecondsOn) {
			setShowExtensionHint(false);
			return;
		}
		let abort = false;
		let timer: number;
		const toggle = () =>
			setShowExtensionHint((oldShow) => {
				if (abort) return oldShow;
				// switch styles while it's invisible
				if (oldShow) {
					setTimeout(() => setExtensionHintVersion((old) => (old + 1) % 2), 500);
				}
				timer = setTimeout(toggle, (oldShow ? settings.SecondsOff : settings.SecondsOn) * 1_000);
				return !oldShow;
			});
		timer = setTimeout(toggle, settings.SecondsOff * 1_000);
		return () => {
			abort = true;
			clearTimeout(timer);
			setShowExtensionHint(false);
		};
	}, [settings.Enabled, settings.SecondsOn, settings.SecondsOff]);

	return (
		<div
			className={`absolute right-0 top-1/2 flex items-end transition duration-500 ${showExtensionHint ? '-translate-y-[80%]' : 'opacity-0 -translate-y-10'}`}
		>
			<div className="relative pr-[180px]">
				{extensionHintVersion === 0 ? <ExtensionHint1 width={396} /> : <ExtensionHint2 width={493} />}
				<div className="-top-[80px] -right-[90px] absolute">
					<img alt="hint gradient" className="w-[230px]" src="/hint-gradient.png" />
				</div>
			</div>
		</div>
	);
}

function PauseOverlay(props: { shouldPause: boolean; popupSettings?: React.MutableRefObject<PopupSettings> }) {
	if (!props.shouldPause) {
		return null;
	}
	const imgHeight = 104;
	const imgWidth = 300;
	return (
		<div
			className="absolute h-auto"
			style={{
				// center of the popup slot
				width: imgWidth,
				top: (props.popupSettings?.current.Y ?? 1000) + (props.popupSettings?.current.Height ?? 10) / 2 - imgHeight / 2,
				left: (props.popupSettings?.current.X ?? 10) + (props.popupSettings?.current.Width ?? 20) / 2 - imgWidth / 2,
			}}
		>
			<img alt="paused" src={tangiaPaused} className="w-full h-full" />
		</div>
	);
}

// check for super old software. Some streaming software is embedding ancient versions of Chrome
function isOutdatedBrowser() {
	return !Promise || !Promise.any;
}

function OutdatedBrowserWarning() {
	return (
		<div className="bg-red-900 text-red-50 text-2xl p-10 m-10 rounded border-8 border-red-100">
			<h1 className="font-semibold">WOW, that's some old software you're using!</h1> Please update your streaming software and/or consider switching to OBS,
			Streamlabs or Twitch Studio
		</div>
	);
}

// since we introduced iframe auto-scaling we don't really need this anymore
function RequiredResolutionCheck() {
	return (
		<div className="2xl:hidden fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[60px] shadow-2xl rounded-2xl bg-red-100 border-2 border-red-600 text-center px-6 py-4">
			<div className="flex flex-col">
				<h1 className="whitespace-nowrap">Hey! You need to set the following properties:</h1>
				<div>
					<strong>width:</strong> 1920
				</div>
				<div>
					<strong>height:</strong> 1080
				</div>
				<div style={{ fontSize: '24px', marginTop: '2em' }}>
					Currently: {document?.body?.getBoundingClientRect()?.width} x {document?.body?.getBoundingClientRect()?.height}
				</div>
			</div>
		</div>
	);
}

const createIframeStyle = (
	eventType: 'overlay_spot' | 'overlay_popup' | 'dungeon',
	spotSettings: typeof spotSettingsDefault,
	popupSettings: typeof popupSettingsDefault,
	placement?: {
		X: number;
		Y: number;
		Width: number;
		Height: number;
		Rotation?: number;
	},
	dungeonPlacement?: PositionSettings,
) => {
	function getPos(s: PositionSettings) {
		const safeWidth = limit(placement?.Width || s.Width, s.Width * 0.3, s.Width * 2);
		const safeHeight = limit(placement?.Height || s.Height, s.Height * 0.3, s.Height * 2);
		return {
			Y: limit(placement?.Y || s.Y, -safeHeight / 2, 1080 - safeHeight / 2),
			X: limit(placement?.X || s.X, -safeWidth / 2, 1920 - safeWidth / 2),
			Width: safeWidth,
			Height: safeHeight,
		};
	}

	if (eventType === 'overlay_spot') {
		const p = getPos(spotSettings);
		return {
			width: `800px`,
			height: `800px`,
			top: `${p.Y}px`,
			left: `${p.X}px`,
			transform: `translate(-50%, -50%) scale(${Math.min(p.Width, p.Height) / 800}) translate(50%, 50%) rotate(${placement?.Rotation ?? 0}deg)`,
			transformOrigin: `center`,
		};
	} else if (eventType === 'overlay_popup') {
		const p = getPos(popupSettings);
		return {
			width: `800px`,
			height: `525px`,
			top: `${p.Y}px`,
			left: `${p.X}px`,
			transform: `scale(${Math.min(p.Width) / 800})`,
			transformOrigin: `top left`,
		};
	} else if (eventType === 'dungeon' && dungeonPlacement) {
		const p = getPos(dungeonPlacement);
		return {
			width: `900px`,
			height: `1050px`,
			top: `${p.Y}px`,
			left: `${p.X}px`,
			transform: `scale(${Math.min(p.Width) / 900})`,
			transformOrigin: `top left`,
		};
	}
	return {};
};

const limit = (x: number, low: number, high: number) => {
	if (x < low) return low;
	if (x > high) return high;
	return x;
};

// global deduplication
const dedupeMap = JSON.parse(localStorage.getItem('dedupeMap') ?? '{}') as Record<number, boolean>;
const storeEventHandled = (ts: number) => {
	// remove old entries from map, keep newest ten
	Object.keys(dedupeMap)
		.sort((a, b) => Number(a) - Number(b))
		.slice(0, -10)
		.forEach((k) => delete dedupeMap[Number(k)]);

	dedupeMap[ts] = true;
	localStorage.setItem('dedupeMap', JSON.stringify(dedupeMap));
};
const confirmEventHandled = async (ackNackSkip: 'ack' | 'nack' | 'skip', event: any, reason?: string) => {
	if (event.EventID) {
		for (let attempt = 0; attempt < 3; attempt++) {
			try {
				await ackLinkApi.postEventAckNackSkip(ackNackSkip, event.EventID, reason);
				break;
			} catch (e) {
				if (attempt === 2) {
					throw e;
				}
				console.error('Failed to send ack/nack/skip to backend, retrying', e);
				await new Promise((r) => setTimeout(r, 1_000));
			}
		}
	}
	if (event.UnixMS > 0) {
		storeEventHandled(event.UnixMS);
	}
	// Wait a second to allow async ack to be processed in the backend
	// (this just reduces the number of unnecessary calls, deduping will handle timing issues)
	await new Promise((r) => setTimeout(r, 1_000));
};

async function refreshPage(start: Date) {
	let u = new URL(window.location.href);
	u.searchParams.set('nocache', 's_' + start.getTime());
	window.location.href = u.toString();
	// reload should happen here and kill our process
	await new Promise((r) => setTimeout(r, 10_000));
	console.log('ups, window reload failed');
}

function newSplitPromise<T>() {
	let resolve: (val: T) => void;
	let reject: (reason?: any) => void;
	const p = new Promise<T>((r, j) => {
		resolve = r;
		reject = j;
	});
	return [p, resolve!, reject!] as const;
}

export default StreamFrame;
